import type { ApprovalView } from '@omni/shared';
import { zh } from '../i18n';

/**
 * Local notifications for approvals that arrive while the tab is hidden
 * (SPEC §10). Permission is asked **only after the first resolved approval** —
 * a permission prompt on first paint is the fastest way to get denied forever,
 * and until the user has answered one approval they have no idea what they
 * would be allowing.
 */

const ASKED_KEY = 'omni.notifications.asked';

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function notificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function hasAskedForPermission(): boolean {
  return storage()?.getItem(ASKED_KEY) === '1';
}

/** Call once, after the user has resolved their first approval. */
export function requestPermissionAfterFirstResolution(): void {
  if (!notificationsSupported() || hasAskedForPermission()) return;
  storage()?.setItem(ASKED_KEY, '1');
  if (Notification.permission === 'default') {
    void Notification.requestPermission().catch(() => undefined);
  }
}

export function notifyApproval(approval: ApprovalView): void {
  if (!notificationsSupported()) return;
  if (Notification.permission !== 'granted') return;
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
  try {
    new Notification(zh.approval.notificationTitle, {
      body: zh.approval.notificationBody(approval.label),
      tag: `omni-approval-${approval.id}`,
      icon: '/icons/icon-192.png',
    });
  } catch {
    // Some browsers throw when constructing a Notification outside a SW.
  }
}
